import { ChangeEvent, KeyboardEvent } from "react";
import cn from "classnames";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";

import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

import useStyles from "../Chatbot.styles";
import { ChatMessageProps } from "./ChatMessage";
import { useIntl } from "react-intl";

interface ChatbotFooterProps {
    isOnline: boolean;
    isLoading: boolean;
    message: string;
    messages: ChatMessageProps[];
    onMessageChange: (value: string) => void;
    onSendMessage: () => void;
    onAbortMessage?: () => void;
}

const ChatbotFooter = ({
    isOnline,
    isLoading,
    message,
    messages,
    onMessageChange,
    onSendMessage,
    onAbortMessage,
}: ChatbotFooterProps) => {
    const classes = useStyles();
    const intl = useIntl();

    const handleMessageChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onMessageChange(event.target.value);
    };

    const handleMessageKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" || event.shiftKey) {
            return;
        }

        event.preventDefault();
        onSendMessage();
    };

    const handleDownloadConversation = () => {
        if (!messages.length) {
            return;
        }

        const conversation = messages
            .map(({ type, content }) => {
                const section = type === "user" ? "User" : "eddy";

                return `${section}: ${content.trim()}`;
            })
            .join("\n\n");

        const blob = new Blob([conversation], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const dateStamp = new Date().toISOString().slice(0, 10);

        link.href = url;
        link.download = `eddy-conversation-${dateStamp}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Box className={classes.footer}>
            <TextField
                fullWidth
                multiline
                maxRows={5}
                value={message}
                className={classes.input}
                onClick={(event) => event.stopPropagation()}
                onChange={handleMessageChange}
                onKeyDown={handleMessageKeyDown}
                placeholder={
                    isOnline
                        ? intl.formatMessage({ id: "chatbot.input.placeholder.online" })
                        : intl.formatMessage({ id: "chatbot.input.placeholder.offline" })
                }
                variant="outlined"
                disabled={!isOnline || isLoading}
                inputProps={{ "aria-label": "Message Eddy" }}
            />

            <IconButton
                className={cn(classes.footerButton, classes.sendButton, {
                    [classes.stopButton]: isLoading,
                })}
                disabled={!isOnline || (isLoading && messages.length <= 1) || (!isLoading && !message.trim())}
                onClick={isLoading ? onAbortMessage : onSendMessage}
                aria-label={
                    isLoading
                        ? intl.formatMessage({ id: "chatbot.ariaLabel.abort" })
                        : intl.formatMessage({ id: "chatbot.ariaLabel.send" })
                }
            >
                <CustomIcon className={classes.sendIcon} icon={isLoading ? icons.iconsStop : icons.arrowSend} />
            </IconButton>

            <Tooltip
                arrow
                placement="bottom-end"
                title={intl.formatMessage({ id: "chatbot.tooltip.downloadConversation" })}
                classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
            >
                <IconButton
                    className={cn(classes.footerButton, classes.otherButton)}
                    disabled={!isOnline}
                    onClick={handleDownloadConversation}
                    aria-label={intl.formatMessage({ id: "chatbot.tooltip.downloadConversation" })}
                >
                    <CustomIcon sx={{ fontSize: 16 }} icon={icons.downloadFile} />
                </IconButton>
            </Tooltip>

            {/* NOTE: This button stays hidden until we have a clearer idea of how we want to implement the summarization
            feature. */}
            {/* <Tooltip
                arrow
                placement="bottom-end"
                title="Summarise key points of conversation"
                classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                aria-label="Summarise key points"
            >
                <IconButton
                    className={cn(classes.footerButton, classes.otherButton)}
                    disabled={!isOnline}
                    onClick={() => console.log("summarize key points")}
                    aria-label="Summarise key points"
                >
                    <CustomIcon sx={{ fontSize: 16 }} icon={icons.reportFilled} />
                </IconButton>
            </Tooltip> */}
        </Box>
    );
};

export default ChatbotFooter;
